/**
 * ============================================================================
 *  BILLING CONFIG — every number that decides what Spark charges and what it
 *  costs us lives in this file, and only in this file.
 * ============================================================================
 *
 * Change a price, an allowance or a provider rate here and it spreads to:
 *   - the API's enforcement (core reads PLANS for quotas and persona limits)
 *   - the in-app upgrade page and the public pricing site (both render PLANS)
 *   - cost measurement (the recorder prices every AI and X call from RATES)
 *
 * There are two halves, kept separate on purpose because they answer
 * different questions:
 *
 *   PLANS  — what we CHARGE users, and what their plan ALLOWS them.
 *   RATES  — what providers CHARGE US for the work those users trigger.
 *
 * Margin is the gap between the two. It could not be computed before this
 * file existed, because nothing recorded what anything cost.
 *
 * Rules for editing:
 *   - Numbers only here. Logic reads them; it never re-declares them. Price
 *     tables used to be duplicated across the AI service and every provider,
 *     and had drifted.
 *   - When you change a RATE, update its `source` and `verifiedOn`. A rate with
 *     `verifiedOn: null` is a figure nobody has re-checked, and it says so.
 *   - Where an advertised figure and an enforced one disagree, fix the copy,
 *     not the limit: advertising a ceiling the API will not honour is the
 *     failure this file exists to prevent.
 *
 * This package imports nothing and is bundled into the browser by the app and
 * the marketing site, so keep it plain data.
 */

import {
  type EnforcementPolicy,
  type Plan,
  type Rates,
  SubscriptionTier,
  UNLIMITED,
} from "./types";

/**
 * Safety ceiling the API applies to a plan whose persona limit is unlimited.
 * It is not advertised; it only bounds abuse.
 */
export const PERSONA_HARD_CAP = 200;

// ============================================================================
//  CREDITS — the unit AI usage is metered in
// ============================================================================

/**
 * What one credit is worth, in micro-dollars of actual provider cost.
 *
 * 1 credit = $0.001. Chosen (2026-09-23) as a round, user-legible unit small
 * enough that a typical post costs a two-digit number of credits rather than
 * a fraction of one. Every metered AI action is charged its actual provider
 * cost in credits (`creditsFor`, cost.ts), not a flat per-action weight — so a
 * short refine and a long carousel cost what they actually cost, not the same.
 */
export const CREDIT_MICRO_USD = 1_000;

/**
 * What "about one post" costs, in credits — the figure the UI uses to turn a
 * credit balance back into something people think in ("~40 posts left").
 *
 * Measured 2026-09-22: a 30-article evaluation run against gemini-3.6-flash
 * (packages/eval) averaged $0.0100 (10,000 micro-USD) per generated post, which
 * is exactly 10 credits at the $0.001 rate above. Re-measure and update this
 * — and the plan allowances below, which were sized from it — after a model
 * change moves the real cost per post.
 */
export const STANDARD_POST_CREDITS = 10;

/**
 * What one suggestion (a highlight rewrite, an Ask…, a feedback-panel Fix, or
 * an on-demand "Suggest improvements" run) costs in credits once a plan's
 * free monthly allowance (`monthlySuggestions`) is spent.
 *
 * Provisional: a suggestion is a small, targeted call — one selection and its
 * surrounding paragraph, not a whole draft — so this is set well below
 * `STANDARD_POST_CREDITS` rather than measured. Re-measure against real
 * suggestion calls before launch and correct this and the plan allowances
 * below, per the inline-editor spec's own open question.
 */
export const SUGGESTION_CREDITS = 2;

/**
 * What a Studio Chat message costs in credits once the plan's monthly chat
 * allowance (`monthlyChatMessages`) is spent. Flat per mode, not settled to
 * the call's cost, because the composer quotes the price before the user
 * sends: an Ask is a short answer, an Edit returns a structured proposal, and
 * an Edit that spans several tabs reads and writes about that many posts.
 *
 * Provisional, from the spec's token estimates (about 3, 5 and 13 credits at
 * gemini-2.5-flash rates); re-measure on staging and correct these together
 * with the allowances below.
 */
export const CHAT_ASK_CREDITS = 3;
export const CHAT_EDIT_CREDITS = 5;
/** Added to an Edit's price for each tab beyond the first. */
export const CHAT_EDIT_EXTRA_TAB_CREDITS = 3;

/**
 * What the steps of a long-form piece cost in credits, flat per step so the
 * panel can quote the price before each one. The outline is one short
 * structured answer; a section of up to `PIECE_SECTION_BASE_WORDS` words costs
 * `PIECE_SECTION_CREDITS` (about a post and a half, for the outline and the
 * running summary it carries) and each further `PIECE_SECTION_EXTRA_WORDS`
 * words adds `PIECE_SECTION_EXTRA_CREDITS`. Writing a section again costs the
 * same as writing it.
 *
 * Provisional, like the chat prices above; re-measure on a real run.
 */
export const PIECE_OUTLINE_CREDITS = 3;
export const PIECE_SECTION_CREDITS = 15;
export const PIECE_SECTION_BASE_WORDS = 500;
export const PIECE_SECTION_EXTRA_WORDS = 250;
export const PIECE_SECTION_EXTRA_CREDITS = 5;
/** Suggesting a piece's search fields is billed as one section. */
export const PIECE_SEO_CREDITS = PIECE_SECTION_CREDITS;

/** What one section of `words` target words costs in credits. */
export function pieceSectionCredits(words?: number): number {
  const target = Math.max(0, Math.floor(words ?? 0));
  const extra = Math.max(0, target - PIECE_SECTION_BASE_WORDS);
  return (
    PIECE_SECTION_CREDITS +
    Math.ceil(extra / PIECE_SECTION_EXTRA_WORDS) * PIECE_SECTION_EXTRA_CREDITS
  );
}

/**
 * What a Metrics digest task's one small model call claims, in credits. The
 * numbers it summarises cost nothing; only the phrasing is a model call.
 */
export const TASK_DIGEST_CREDITS = 2;

/**
 * What proposing the three directions for a post image costs, in credits: one
 * short text call, flat so the dialog can show the price before the user asks.
 */
export const IMAGE_DIRECTIONS_CREDITS = 2;

/**
 * What one generated image costs the user, in credits, at the default
 * (Fast) tier: the model's $0.02 at one credit per $0.001. A round of four is
 * 80. Flat rather than settled to the measured cost, so the price shown
 * before generating is the price charged; the ledger still records the real
 * spend for margin reporting.
 */
export const IMAGE_CREDITS = 20;

/**
 * What one candidate avatar costs the user, in credits: the avatar model
 * (a Gemini image model, which takes the mascot and the headshot as input
 * pictures) costs about $0.037 a candidate, so the same
 * one-credit-per-$0.001 rule, rounded up, gives 40. The default run of two is 80, so a Free month (100 credits) still
 * covers one. Flat, like `IMAGE_CREDITS`.
 */
export const AVATAR_CREDITS = 40;

/** The image model used until a better tier is offered. */
export const DEFAULT_IMAGE_MODEL = "imagen-4.0-fast-generate-001";

/**
 * The model that draws an avatar: the newest generally available Gemini
 * image model on Vertex, which takes input pictures (the mascot for style,
 * the headshot for the person) and answers with a picture. Imagen 4 only
 * takes a text prompt, and Imagen 3 customization is deprecated.
 * `IMAGE_AVATAR_MODEL` overrides it; a value starting `imagen-` selects the
 * Imagen 3 customization path instead.
 */
export const DEFAULT_AVATAR_IMAGE_MODEL = "gemini-nano-banana-2.1";

// ============================================================================
//  PLANS — what we charge, and what each plan allows
// ============================================================================

export const PLANS: readonly Plan[] = [
  {
    key: SubscriptionTier.FREE,
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    tagline: "Perfect for trying out Spark.",
    cta: "Current Plan",
    // Previously advertised "LinkedIn only" while the API let Free post to X
    // without limit — the most expensive thing the product does, uncapped, on
    // the plan that pays nothing. Now a small allowance, and the copy says so.
    // Credits: the old 10 generations/month x STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 100,
      monthlyGenerations: 10,
      personas: 2,
      monthlyXPosts: 5,
      monthlySuggestions: 30,
      monthlyChatMessages: 30,
      historyRetentionDays: 30,
      namedVersionsPerDraft: 5,
      voices: 1,
      rooms: 0,
      roomMembers: 0,
      activeTasks: 5,
      shortestTaskRepeat: "weekly",
      workflows: 1,
    },
    extraFeatures: [
      { label: "Community support", included: true },
      { label: "Analytics", included: false },
      { label: "Scheduling & calendar", included: true },
    ],
  },
  {
    key: SubscriptionTier.PRO,
    name: "Pro",
    monthlyPrice: 8,
    annualPrice: 6.5,
    tagline: "Everything you need to grow your presence.",
    badge: "Most Popular",
    highlighted: true,
    cta: "Upgrade to Pro",
    // Persona ceilings are a product decision (Free 2, Pro 5, Team 25,
    // Enterprise unlimited), not a cost one; personas cost nothing to hold.
    // X posts sized so that even if every post has a link, X costs about
    // half the plan price. Credits: the old 100 generations/month x
    // STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 1_000,
      monthlyGenerations: 100,
      personas: 5,
      monthlyXPosts: 20,
      monthlySuggestions: 300,
      monthlyChatMessages: 300,
      historyRetentionDays: 365,
      namedVersionsPerDraft: UNLIMITED,
      voices: 5,
      rooms: 1,
      roomMembers: 5,
      activeTasks: 50,
      shortestTaskRepeat: "daily",
      workflows: 10,
    },
    extraFeatures: [
      { label: "LinkedIn and X", included: true },
      { label: "Priority support", included: true },
      { label: "Basic analytics (coming soon)", included: true },
      { label: "Scheduling & calendar", included: true },
    ],
  },
  {
    key: SubscriptionTier.TEAM,
    name: "Team",
    monthlyPrice: 29,
    annualPrice: 24,
    tagline: "Collaborate with your entire team.",
    cta: "Upgrade to Team",
    // Credits: the old 500 generations/month x STANDARD_POST_CREDITS.
    limits: {
      monthlyCredits: 5_000,
      monthlyGenerations: 500,
      personas: 25,
      monthlyXPosts: 70,
      monthlySuggestions: 1_500,
      monthlyChatMessages: 1_500,
      historyRetentionDays: UNLIMITED,
      namedVersionsPerDraft: UNLIMITED,
      voices: 20,
      rooms: 5,
      roomMembers: 25,
      activeTasks: 200,
      shortestTaskRepeat: "daily",
      workflows: 50,
    },
    extraFeatures: [
      { label: "Everything in Pro", included: true },
      { label: "5 rooms, 25 members each (coming soon)", included: true },
      { label: "Advanced analytics (coming soon)", included: true },
      { label: "Team admin tools (coming soon)", included: true },
    ],
  },
  {
    key: SubscriptionTier.ENTERPRISE,
    name: "Enterprise",
    monthlyPrice: null,
    annualPrice: null,
    tagline: "Dedicated support & infrastructure at scale.",
    cta: "Contact Sales",
    // Credits and personas are advertised as unlimited. The API still stops
    // at PERSONA_HARD_CAP so a runaway client cannot create rows forever.
    limits: {
      monthlyCredits: UNLIMITED,
      monthlyGenerations: UNLIMITED,
      personas: UNLIMITED,
      monthlyXPosts: UNLIMITED,
      monthlySuggestions: UNLIMITED,
      monthlyChatMessages: UNLIMITED,
      historyRetentionDays: UNLIMITED,
      namedVersionsPerDraft: UNLIMITED,
      voices: UNLIMITED,
      rooms: UNLIMITED,
      roomMembers: UNLIMITED,
      activeTasks: UNLIMITED,
      shortestTaskRepeat: "daily",
      workflows: UNLIMITED,
    },
    extraFeatures: [
      { label: "Everything in Team", included: true },
      { label: "Unlimited rooms & members (coming soon)", included: true },
      { label: "Custom SLA", included: true },
      { label: "Dedicated success manager", included: true },
      { label: "24 / 7 phone support", included: true },
    ],
  },
] as const;

// ============================================================================
//  CUSTOM PRICES — a per-account monthly price set by an admin
// ============================================================================

/**
 * The most an admin may set as one account's monthly price, in USD.
 *
 * A custom price replaces the list price for one account: a discount, or $0
 * for a trial. The cap is there to catch a price typed in cents or micro-
 * dollars by mistake, not to bound what Enterprise could be charged; raise it
 * if a real contract exceeds it.
 */
export const CUSTOM_PRICE_CAP_USD = 10_000;

// ============================================================================
//  ENFORCEMENT — what happens at a limit
// ============================================================================

/**
 * Graduated, not a wall.
 *
 * People exceed their limits routinely, so hitting one is not an error and
 * does not page anyone. Past the limit a user gets a grace allowance to finish
 * what they are doing — the pricing FAQ promised this long before the code did
 * it — during which interactive requests are spaced out. Past the allowance,
 * requests are refused with the reset date.
 */
export const ENFORCEMENT: EnforcementPolicy = {
  grace: {
    ratio: 0.1,
    minimumGenerations: 2,
    // Two standard posts' worth of credits — the same floor as before,
    // re-denominated: STANDARD_POST_CREDITS * 2.
    minimumCredits: 20,
    minimumXPosts: 1,
  },
  throttle: {
    minIntervalSeconds: 60,
  },
};

// ============================================================================
//  REGIONAL PRICES, TOP-UPS AND DUNNING
// ============================================================================

/**
 * Monthly USD price per regional tier (index 0 is tier 1), set by hand and
 * reviewed quarterly rather than computed from the list price: a computed
 * 29 x 0.7 is $20.30, and nobody should be billed a figure no one chose.
 */
export const REGIONAL_MONTHLY_USD = {
  pro: [8, 5.6, 4, 3.2],
  team: [29, 20, 14.5, 11.6],
} as const;

/** What each regional tier pays as a share of tier 1; applied to annual prices and credit packs. */
export const REGIONAL_MULTIPLIER = [1, 0.7, 0.5, 0.4] as const;

/**
 * One-off credit packs. Bought credits never expire while the account exists
 * and are spent after the monthly allowance, so the price is per pack, not per
 * month. USD at tier 1; a lower tier pays the same multiplier as a plan.
 */
export const CREDIT_PACKS = [
  { id: "credits-1000", credits: 1_000, priceUsd: 3 },
  { id: "credits-5000", credits: 5_000, priceUsd: 12 },
  { id: "credits-20000", credits: 20_000, priceUsd: 40 },
] as const;

/**
 * What happens after a card is declined, in days from the first failure.
 *
 * The plan stays fully active for `softLimitAfterDays` so a card that expired
 * overnight does not cost anyone a feature; then allowances drop to Free's
 * (nothing is removed) until the card is fixed; at `cancelAfterDays` the
 * subscription ends and the account is on Free. `emailDays` are the days a
 * reminder goes out, the first being the failure itself.
 */
export const DUNNING = {
  emailDays: [0, 3, 10],
  softLimitAfterDays: 7,
  cancelAfterDays: 14,
} as const;

/** Days before a trial or comped month ends that the "ending soon" notice goes out. */
export const TRIAL_ENDING_NOTICE_DAYS = 3;

// ============================================================================
//  RATES — what providers charge us
// ============================================================================

export const RATES: Rates = {
  ai: {
    models: {
      /**
       * The primary model: every generation, refine, variation and carousel,
       * and (since its predecessor was shut down) source extraction.
       *
       * The previous figures here were $0.15 / $0.60 — preview-era prices —
       * and cost was computed from visible output only, dropping thinking
       * tokens that Google bills at the output rate. Together that recorded a
       * typical post at about a fifteenth of its real cost.
       */
      "gemini-2.5-flash": {
        provider: "gemini",
        inputUsdPerMillion: 0.3,
        outputUsdPerMillion: 2.5,
        thinkingBilledAsOutput: true,
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      /**
       * Gemini 3 Flash — Google's named replacement for 2.5 Flash, and the
       * target of the staged migration. Not the default yet: these prices are
       * from the Gemini API and Vertex availability in our region still has to
       * be confirmed on staging before production follows.
       *
       * The listed prices are promotional and double on 1 January 2027.
       */
      "gemini-3.6-flash": {
        provider: "gemini",
        inputUsdPerMillion: 0.75,
        outputUsdPerMillion: 3.75,
        thinkingBilledAsOutput: true,
        scheduledChange: {
          from: "2027-01-01",
          inputUsdPerMillion: 1.5,
          outputUsdPerMillion: 7.5,
        },
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      /**
       * Gemini 3 Flash-Lite — cheaper per token than the 2.5 Flash in use
       * today, at lower quality. Priced here so a quality-versus-cost
       * comparison can be made from measured figures rather than guessed.
       */
      "gemini-3.1-flash-lite": {
        provider: "gemini",
        inputUsdPerMillion: 0.25,
        outputUsdPerMillion: 1.5,
        thinkingBilledAsOutput: true,
        source: "https://ai.google.dev/gemini-api/docs/pricing",
        verifiedOn: "2026-09-14",
      },

      /**
       * Text embeddings for Layers retrieval (core/src/embeddings). Priced per
       * input token only. There is no output, so the output rate is the smallest
       * non-zero value the rate-card rule accepts. Half this in batch, which the
       * scheduled sync will use once it exists.
       */
      "gemini-embedding-2": {
        provider: "gemini",
        inputUsdPerMillion: 0.2,
        outputUsdPerMillion: 0.000001,
        thinkingBilledAsOutput: false,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-09-30",
      },

      // Fallbacks. The router only reaches these when their API keys are
      // configured and Gemini has failed — which is when the ledger is least
      // able to afford a guess, so these are verified like the primary.
      "gpt-4o-mini": {
        provider: "openai",
        inputUsdPerMillion: 0.15,
        outputUsdPerMillion: 0.6,
        thinkingBilledAsOutput: false,
        source: "https://developers.openai.com/api/docs/pricing",
        verifiedOn: "2026-09-21",
      },
      /**
       * Replaces claude-3-5-sonnet-20241022, which Anthropic retired on
       * 28 October 2025: every fallback to it failed, priced at $3 / $15
       * from a figure nobody had checked.
       */
      "claude-haiku-4-5-20251001": {
        provider: "anthropic",
        inputUsdPerMillion: 1,
        outputUsdPerMillion: 5,
        thinkingBilledAsOutput: false,
        source: "https://platform.claude.com/docs/en/about-claude/pricing",
        verifiedOn: "2026-09-21",
      },
      "claude-sonnet-5": {
        provider: "anthropic",
        inputUsdPerMillion: 2,
        outputUsdPerMillion: 10,
        thinkingBilledAsOutput: false,
        source: "https://platform.claude.com/docs/en/about-claude/pricing",
        verifiedOn: "2026-09-21",
      },

      /**
       * OpenAI's open-weight models, served on Vertex AI Model-as-a-Service.
       * Not a fallback for product traffic — see `vertex-maas.ts` — used
       * only as the generation eval harness's independent voice grader,
       * where a Gemini model grading Gemini output would not be one.
       * Reasoning tokens are reported as a subset of `completion_tokens`
       * (the OpenAI convention), already inside `completionTokens`, so
       * `thinkingBilledAsOutput: false` here is what stops them being
       * counted twice (contrast Gemini, which reports them separately and
       * bills them additionally).
       */
      "openai/gpt-oss-120b-maas": {
        provider: "vertex-maas",
        inputUsdPerMillion: 0.09,
        outputUsdPerMillion: 0.36,
        thinkingBilledAsOutput: false,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-09-23",
      },
      "openai/gpt-oss-20b-maas": {
        provider: "vertex-maas",
        inputUsdPerMillion: 0.07,
        outputUsdPerMillion: 0.25,
        thinkingBilledAsOutput: false,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-09-23",
      },
    },
  },

  /**
   * X API, pay-per-use (X's default billing since February 2026).
   *
   * X is the most expensive thing Spark does per operation. A post containing
   * a URL costs roughly thirteen times a plain post, and around sixty times the
   * AI call that wrote it — and repurposed content often links its source.
   *
   * OAuth token exchange and refresh are not listed as billable, so the adapter
   * does not record them. Delete and media upload are not listed either; they
   * are recorded as unpriced rather than assumed free.
   *
   * LinkedIn publishing has no per-call fee, so it has no rates here.
   */
  transcription: {
    models: {
      /** Dictation's server fallback. Audio is priced by its length. */
      "gpt-4o-mini-transcribe": {
        provider: "openai",
        usdPerMinute: 0.003,
        source: "https://developers.openai.com/api/docs/pricing",
        verifiedOn: "2026-10-22",
      },
    },
  },

  x: {
    billing: "pay-per-use",
    source: "https://docs.x.com/x-api/getting-started/pricing",
    verifiedOn: "2026-09-16",
    postCreateUsd: 0.015,
    postCreateWithUrlUsd: 0.2,
    userReadUsd: 0.01,
    // "Post Read: $0.005 per resource". X prices some user-specific reads at
    // $0.001 ("owned reads": /2/users/{id}/tweets and similar), but reading a
    // post by id is not one of those, so this is the rate that applies to us.
    postReadUsd: 0.005,
    // X publishes no charge for deleting a post, and its webhook documentation
    // lists post.delete as not billed. Zero, not null: we know this one is
    // free, rather than not knowing what it costs.
    postDeleteUsd: 0,
    // Still no published price for media upload.
    mediaUploadUsd: null,
  },

  /**
   * Images are billed per picture, not per token.
   */
  images: {
    models: {
      "imagen-4.0-fast-generate-001": {
        provider: "vertex-imagen",
        usdPerImage: 0.02,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-10-08",
      },
      // Standard PayGo, global: a 1K picture is 1120 output tokens at $30 per
      // million ($0.0336), and a candidate carries two input pictures of
      // 1120 tokens each at $1.50 per million ($0.0034), $0.037 in all.
      // `AVATAR_CREDITS` (40) covers it at one credit per $0.001. Not yet
      // checked against a Vertex invoice.
      "gemini-nano-banana-2.1": {
        provider: "vertex-gemini",
        usdPerImage: 0.037,
        source: "https://docs.cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: "2026-10-26",
      },
      // The previous avatar model, still selectable with IMAGE_AVATAR_MODEL.
      // Deprecated by Google; its price is the published $0.04.
      "imagen-3.0-capability-001": {
        provider: "vertex-imagen",
        usdPerImage: 0.04,
        source: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
        verifiedOn: null,
      },
    },
  },

  /**
   * Cloudflare Browser Rendering REST API — the rendered-fetch step of web
   * extraction (a real browser loads a page the direct fetch couldn't read).
   * 10 browser-minutes a day are free; this is what Cloudflare bills per
   * hour past that. Imports are never charged to the user — see this
   * module's own header — so this rate feeds cost measurement only.
   */
  cloudflare: {
    billing: "pay-per-use",
    source:
      "https://developers.cloudflare.com/browser-rendering/platform/limits/",
    verifiedOn: "2026-10-08",
    browserHourUsd: 0.09,
  },
};
